import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet, apiPost } from '../api';
import { unwrapData } from '@wapharma/shared';;
import { getDisplayError } from '../apiErrors';
import { getToken } from '../auth';
import { IMPORT_JOB_STORAGE_KEY } from '../components/useProductBulkImport';
import { useToast } from '../contexts/ToastContext';
import { deferAfterPaint } from '../lib/deferAfterPaint';
import { playNotificationSound } from '../lib/notificationSound';
import {
  CATALOG_INBOX_OWNER,
  catalogImportAckType,
} from '../utils/catalogImportInboxAck';
import {
  CATALOG_IMPORT_SEEN_EVENT,
  dispatchCatalogImportSeen,
  markCatalogImportJobNotified,
  readNotifiedCatalogImportJobIds,
  readSeenCatalogImportJobIds,
} from '../utils/catalogImportSeenStorage';

export type ImportJobStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

export type TrackedImportJob = {
  jobId: string;
  status: ImportJobStatus;
  fileName: string;
  totalRows: number;
  processedRows: number;
  createdCount: number;
  failedCount: number;
  errorMessage: string | null;
  provisioning: import('../components/useProductBulkImport').ImportProvisioning | null;
  sampleErrors: import('../components/useProductBulkImport').ImportRowResult[];
};

const POLL_MS = 1000;

function isTerminalImportStatus(status: ImportJobStatus) {
  return status === 'COMPLETED' || status === 'FAILED' || status === 'CANCELLED';
}

function notifyImportFinished(job: TrackedImportJob) {
  playNotificationSound();
  const title =
    job.status === 'COMPLETED'
      ? 'Import produits terminé'
      : job.status === 'FAILED'
        ? 'Import produits échoué'
        : 'Import produits annulé';
  const body =
    job.status === 'COMPLETED'
      ? `${job.createdCount.toLocaleString('fr-FR')} créé(s), ${job.failedCount.toLocaleString('fr-FR')} échec(s) — ${job.fileName}`
      : job.errorMessage ?? job.fileName;

  if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
    try {
      new Notification(title, { body, tag: `catalog-import-${job.jobId}` });
    } catch {
      /* ignore */
    }
  }
}

function readStoredImportJobId(): string | null {
  try {
    return localStorage.getItem(IMPORT_JOB_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Polls catalog import jobs for inbox progress + completion toast (survives navigation).
 */
export function useCatalogImportJobInbox() {
  const token = getToken();
  const { showToast } = useToast();
  const [job, setJob] = useState<TrackedImportJob | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const notifiedRef = useRef<Set<string>>(readNotifiedCatalogImportJobIds());
  const pollEnabledRef = useRef(Boolean(readStoredImportJobId()));

  const dismissResult = useCallback(() => {
    if (!job?.jobId) return;
    const jobId = job.jobId;
    const status = job.status;
    dispatchCatalogImportSeen(jobId);
    if (token) {
      void apiPost(
        '/inbox/seen',
        {
          items: [
            {
              ownerRole: CATALOG_INBOX_OWNER,
              type: catalogImportAckType(status),
              entityId: jobId,
            },
          ],
        },
        token,
      ).catch(() => {
        /* localStorage fallback remains */
      });
    }
    notifiedRef.current.add(jobId);
    markCatalogImportJobNotified(jobId);
    setJob(null);
    setErr(null);
    try {
      localStorage.removeItem(IMPORT_JOB_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    window.dispatchEvent(new Event('wapharma-inbox-reload'));
  }, [job?.jobId, job?.status, token]);

  useEffect(() => {
    if (!token) {
      setJob(null);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const stopPolling = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const startPolling = () => {
      if (timer || document.hidden) return;
      timer = setInterval(() => void poll(), POLL_MS);
    };

    const mapJob = (raw: Record<string, unknown>): TrackedImportJob => ({
      jobId: String(raw.jobId ?? raw.id ?? ''),
      status: raw.status as ImportJobStatus,
      fileName: String(raw.fileName ?? ''),
      totalRows: Number(raw.totalRows ?? 0),
      processedRows: Number(raw.processedRows ?? 0),
      createdCount: Number(raw.createdCount ?? 0),
      failedCount: Number(raw.failedCount ?? 0),
      errorMessage: raw.errorMessage != null ? String(raw.errorMessage) : null,
      provisioning: (raw.provisioning as TrackedImportJob['provisioning']) ?? null,
      sampleErrors: (raw.sampleErrors as TrackedImportJob['sampleErrors']) ?? [],
    });

    const pickActiveJob = (jobs: TrackedImportJob[]): TrackedImportJob | null => {
      const seenIds = readSeenCatalogImportJobIds();

      const inFlight = jobs.find((j) => j.status === 'PENDING' || j.status === 'RUNNING');
      if (inFlight) return inFlight;

      let stored: string | null = null;
      try {
        stored = localStorage.getItem(IMPORT_JOB_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      if (stored && !seenIds.has(stored)) {
        const byId = jobs.find((j) => j.jobId === stored);
        if (byId) return byId;
      }

      const recentDone = jobs.find(
        (j) => isTerminalImportStatus(j.status) && !seenIds.has(j.jobId),
      );
      return recentDone ?? null;
    };

    const poll = async () => {
      if (!pollEnabledRef.current) return;
      try {
        let jobs: TrackedImportJob[] = [];
        const listRaw = await apiGet<{ data: unknown[] } | unknown[]>(
          '/catalog/admin/products/import/jobs',
          token,
        );
        const list = unwrapData(listRaw) as Record<string, unknown>[];
        jobs = list.map(mapJob);

        let active = pickActiveJob(jobs);

        if (!active) {
          let stored: string | null = null;
          try {
            stored = localStorage.getItem(IMPORT_JOB_STORAGE_KEY);
          } catch {
            /* ignore */
          }
          if (stored && !readSeenCatalogImportJobIds().has(stored)) {
            try {
              const oneRaw = await apiGet<{ data: unknown } | unknown>(
                `/catalog/admin/products/import/jobs/${stored}`,
                token,
              );
              active = mapJob(unwrapData(oneRaw) as Record<string, unknown>);
            } catch {
              try {
                localStorage.removeItem(IMPORT_JOB_STORAGE_KEY);
              } catch {
                /* ignore */
              }
            }
          }
        }

        if (cancelled) return;

        if (active) {
          pollEnabledRef.current = true;
          const seenIds = readSeenCatalogImportJobIds();
          const terminal = isTerminalImportStatus(active.status);

          if (terminal && seenIds.has(active.jobId)) {
            setJob(null);
            setErr(null);
            stopPolling();
            try {
              localStorage.removeItem(IMPORT_JOB_STORAGE_KEY);
            } catch {
              /* ignore */
            }
            return;
          }

          setJob(active);
          setErr(null);

          if (terminal && !notifiedRef.current.has(active.jobId)) {
            notifiedRef.current.add(active.jobId);
            markCatalogImportJobNotified(active.jobId);
            try {
              localStorage.removeItem(IMPORT_JOB_STORAGE_KEY);
            } catch {
              /* ignore */
            }

            if (active.status === 'COMPLETED') {
              showToast(
                `Import terminé — ${active.createdCount.toLocaleString('fr-FR')} produit(s) créé(s).`,
                { tone: 'success' },
              );
            } else if (active.status === 'FAILED') {
              showToast(active.errorMessage ?? 'Import échoué.', { tone: 'error' });
            }
            notifyImportFinished(active);
            window.dispatchEvent(new Event('wapharma-inbox-reload'));
          }

          if (active.status === 'PENDING' || active.status === 'RUNNING') {
            startPolling();
          } else {
            stopPolling();
            if (terminal) {
              try {
                localStorage.removeItem(IMPORT_JOB_STORAGE_KEY);
              } catch {
                /* ignore */
              }
            }
          }
        } else {
          setJob(null);
          stopPolling();
          try {
            localStorage.removeItem(IMPORT_JOB_STORAGE_KEY);
          } catch {
            /* ignore */
          }
        }
      } catch (e: unknown) {
        if (cancelled) return;
        setErr(getDisplayError(e));
        setJob(null);
        try {
          localStorage.removeItem(IMPORT_JOB_STORAGE_KEY);
        } catch {
          /* ignore */
        }
        stopPolling();
      }
    };

    const onImportJobStarted = () => {
      pollEnabledRef.current = true;
      void poll();
    };

    const onVisibility = () => {
      if (document.hidden) {
        stopPolling();
        return;
      }
      if (!pollEnabledRef.current) return;
      void poll();
    };

    const onCatalogImportSeen = (event: Event) => {
      const jobId = (event as CustomEvent<{ jobId?: string }>).detail?.jobId;
      if (!jobId) return;
      notifiedRef.current.add(jobId);
      markCatalogImportJobNotified(jobId);
      setJob((prev) => (prev?.jobId === jobId ? null : prev));
      try {
        localStorage.removeItem(IMPORT_JOB_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    };

    const cancelDefer = deferAfterPaint(() => {
      if (!cancelled && pollEnabledRef.current) void poll();
    });

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('wapharma-import-job-started', onImportJobStarted);
    window.addEventListener(CATALOG_IMPORT_SEEN_EVENT, onCatalogImportSeen);
    return () => {
      cancelled = true;
      cancelDefer();
      stopPolling();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('wapharma-import-job-started', onImportJobStarted);
      window.removeEventListener(CATALOG_IMPORT_SEEN_EVENT, onCatalogImportSeen);
    };
  }, [token, showToast]);

  const inFlight = Boolean(job && (job.status === 'PENDING' || job.status === 'RUNNING'));
  const showResult = Boolean(
    job &&
      (job.status === 'COMPLETED' || job.status === 'FAILED' || job.status === 'CANCELLED'),
  );

  return {
    job,
    err,
    inFlight,
    showResult,
    dismissResult,
    importProgress: job
      ? { current: job.processedRows, total: job.totalRows || 1 }
      : null,
  };
}
